import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, FormControl, Button, Modal, Spinner, ProgressBar } from 'react-bootstrap';
import CustomBackground from '../../components/UI/CustomBackground/CustomBackground';
import { useTranslation, Translation } from 'react-i18next';
import graphQLClientInstance from '../../axios-graphql-client';
import classes from './Jurisprudence.module.css';
import Table, { tableOrigin } from '../../components/UI/Table/Table';
import { ReactComponent as InfoCircleLightIcon } from '../../assets/infoCircleLight.svg';
import { Formik } from "formik";
import * as Yup from "yup";
import { withRouter } from 'react-router-dom';
import Loader from '../../components/UI/Loader/Loader';
import {
    backgroundImages,
    renderTooltipOverlay,
    jurisprudenceReturnTypes,
    getSearchJurisprudenceQuery,
    JURISPRUDENCE_SESSION_KEY,
    downloadExcelWorksheetTitles,
    downloadExcelFileNames,
    downloadBlob,
    calcPercentage,
    calcRemaining,
    API_URLS,
  } from '../../shared/utility';
import SearchFormContainer from '../../components/UI/SearchFormContainer/SearchFormContainer';

import { useMatomo } from '@datapunt/matomo-tracker-react';
import CustomDatePicker, { calendarToday } from '../../components/UI/CustomDatePicker/CustomDatePicker';
import Bottleneck from 'bottleneck';
import axios from "axios";
import { generatexlx } from '../../exportXls';
import ResultsHeader from '../../components/UI/ResultsHeader/ResultsHeader';

const Jurisprudence = (props: any) => {
  const { trackPageView } = useMatomo();

  const { t, i18n } = useTranslation();

  const [formValues, setFormValues] = useState([]);
  const formValuesRef = React.useRef({});
  formValuesRef.current = formValues;

  const [isLoading, setIsLoading] = useState(false);
  const [jurisprudenceOptions, setJurisprudenceOptions] = useState<any>({});
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const totalCountRef = React.useRef(0);
  totalCountRef.current = totalCount;

  const [pageSize, setPageSize] = useState(0);
  const pageSizeRef = React.useRef(20)
  pageSizeRef.current = pageSize;

  const [pageIndex, setPageIndex] = useState(0);
  const pageIndexRef = React.useRef(0)
  pageIndexRef.current = pageIndex;

  const resultsRef = React.useRef(null);
  const [data, setData] = useState<any[]|[]>([]);
  const dataRef = React.useRef<any[]>();
  dataRef.current = data;

  const formInitialValues: {[key: string]: any} = {
    article: "",
    subheading: "",
    outcome: "",
    denomination: "",
    decisionDate: [],
    keyword: ""
  };

  const [initialValues, setInitialValues] = useState<{[key: string]: any}>(formInitialValues);

  useEffect(() => {
    trackPageView({documentTitle:'Jurisprudence'});
  }, []);

  useEffect(() => {
    const sessionJurispredence = sessionStorage.getItem(JURISPRUDENCE_SESSION_KEY);
    if (sessionJurispredence) {
      setJurisprudenceOptions(JSON.parse(sessionJurispredence));
    } else {
      getJurisprudence(setJurisprudenceOptions);
    }
  }, []);

  const fetchData = React.useCallback(({ values, pageIndex: tablePageIndex, pageSize: tablePageSize }) => {
    setIsLoading(true);
    setData([]);
    setPageSize(tablePageSize);
    setPageIndex(tablePageIndex);

    if (tablePageIndex === 0) {
      setPageCount(0);
      setTotalCount(0);
    }

    graphQLClientInstance.post(API_URLS.GRAPHQL, getSearchJurisprudenceQuery(values, tablePageSize, tablePageIndex))
    .then((res) => {
      if (res.data.data?.jurisprudenceSearch?.data.length) {
        setData(res.data.data.jurisprudenceSearch.data);
        setTotalCount(res.data.data.jurisprudenceSearch.total);
        setPageCount(Math.ceil(res.data.data.jurisprudenceSearch.total/tablePageSize));
      }
      setIsLoading(false);
    })
    .catch(err => {
      console.log(err);
      setIsLoading(false);
    })
  }, []);

  const refreshData = React.useCallback(() => {
    fetchData({
      pageIndex: pageIndexRef.current,
      pageSize: pageSizeRef.current,
      values: formValuesRef.current
    });
  }, []);

  const form = (
    <Formik
      enableReinitialize = {true}
      initialValues = {initialValues}
      validationSchema = {Yup.object()
    }

    onSubmit = {(values: any, { setSubmitting, resetForm }) => {
        const payload = {
            ...values
        };
        setFormValues(payload);
        setSubmitting(false);
        setIsLoading(true);
      }
    }
    >

    {formProps => (
      <Form
        className={classes.searchForm}
        onSubmit={(e) => {
          formProps.handleSubmit();
          e.preventDefault();
        }}>
        <Row className={classes.searchFieldsRow}>
          <Col md="4" id="articleContainer">
            <Form.Group controlId="searchForm.article">
              <Form.Label className="w-100">
                {t("article")}
                {renderTooltipOverlay(
                  t("tooltips_jurisprudence.article"),
                  <InfoCircleLightIcon />
                )}
              </Form.Label>
              <Form.Control
                as="select"
                name="article"
                value={formProps.values.article}
                onChange={formProps.handleChange}
              >
                <option value={""}></option>
                {
                  Object.keys(jurisprudenceOptions)?.map((val: any, index: number) => {
                    return (
                      <option key={`articles-option-${index}`} value={val}>
                        {val}
                      </option>
                    )
                  })
                }
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md="8" id="subHeadingContainer">
            <Form.Group controlId="searchForm.subHeading">
              <Form.Label className="w-100">
                {t("subheading")}
                {renderTooltipOverlay(
                  t("tooltips_jurisprudence.subheading"),
                  <InfoCircleLightIcon />
                )}
              </Form.Label>
              <Form.Control
                as="select"
                name="subheading"
                value={formProps.values.subheading}
                onChange={formProps.handleChange}
              >
                <option value={""}></option>
                {
                  formProps.values.article ?
                    jurisprudenceOptions[formProps.values.article]?.map((val: any, index: number) => {
                      return (
                        <option key={`subheadings-option-${index}`} value={val}>
                          {val}
                        </option>
                      )
                    })
                  : [...Array.from(new Set(
                        Object.values(jurisprudenceOptions).flat(2)
                      )
                    )].map((val: any, index: number) => {
                    return (
                      <option key={`subheadings-option-${index}`} value={val}>
                        {val}
                      </option>
                    )
                  })
                }
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md="4" id="outcomeContainer">
            <Form.Group controlId="searchForm.outcome">
              <Form.Label className="w-100">
                {t("outcome")}
                {renderTooltipOverlay(
                  t("tooltips_jurisprudence.outcome"),
                  <InfoCircleLightIcon />
                )}
              </Form.Label>
              <Form.Control
                as="select"
                name="outcome"
                value={formProps.values.outcome}
                onChange={formProps.handleChange}
              >
                <option value={""}></option>
                <option value={"suitable"}>{t("suitable")}</option>
                <option value={"not suitable"}>{t("not_suitable")}</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md="8" id="denominationContainer">
            <Form.Group controlId="searchForm.denomination">
              <Form.Label className="w-100">
                {t("denomination")}
                {renderTooltipOverlay(
                  t("tooltips_jurisprudence.denomination"),
                  <InfoCircleLightIcon />
                )}
              </Form.Label>
              <FormControl
                name="denomination"
                as="input"
                type="text"
                value={formProps.values.denomination}
                onChange={formProps.handleChange}
              />
            </Form.Group>
          </Col>
          <Col md="4" id="decisionDateContainer" key="decisionDateContainer">
            <Form.Group>
              <Form.Label className="w-100">
                {t("decision_date")}
                {renderTooltipOverlay(
                  t("tooltips_jurisprudence.decision_date"),
                  <InfoCircleLightIcon />
                )}
              </Form.Label>
              <CustomDatePicker
                maximumDate={calendarToday()}
                name="decisionDate"
                controlId="searchForm.decisionDate"
                setFieldValue={formProps.setFieldValue}
                value={formProps.values.decisionDate}
                placeholderFrom={t("min")}
                placeholderTo={t("max")}
                invalid={
                  formProps.errors.decisionDate && formProps.touched.decisionDate
                }
              />
              <Form.Control.Feedback type="invalid">
                {formProps.errors.decisionDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md="8" id="keywordContainer">
            <Form.Group controlId="searchForm.keyword">
              <Form.Label className="w-100">
                {t("keyword")}
                {renderTooltipOverlay(
                  t("tooltips_jurisprudence.keyword"),
                  <InfoCircleLightIcon />
                )}
              </Form.Label>
              <FormControl
                name="keyword"
                as="input"
                type="text"
                value={formProps.values.keyword}
                onChange={formProps.handleChange}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className={classes.actionsRow}>
          <Col md="12">
            <Button id="submitSearchForm" variant="primary" type="submit" disabled={formProps.isSubmitting} className="float-right">{t("search")}</Button>
            <Button
              id="clearFields"
              variant="link"
              onClick={() => {
                setInitialValues(formInitialValues);
                formProps.resetForm();
              }}
              className="float-right mr-2">
                {t("clear_fields")}
            </Button>
            <Button
              id="help"
              variant="link"
              onClick={() => window.open(`help/${i18n.language}/Jurisprudence_help.html`, '_blank')}
              className="float-right mr-2">
                {t("help")}
            </Button>
            <Button
              id="disclaimer"
              variant="link"
              onClick={() => window.open(`help/${i18n.language}/Jurisprudence_disclaimer.html`, '_blank')}
              className="float-right mr-2">
                {t("disclaimer")}
            </Button>
          </Col>
        </Row>
      </Form>
    )}
    </Formik>
  );

  const selectedColumns = [
    {
      name: jurisprudenceReturnTypes.DENOMINATION,
      label: <Translation>{(translate) =>  translate('denomination')}</Translation>,
      enabled: true,
      isDefault: true,
      disableSortBy: true,
      defaultOrder: 0
    },
    {
      name: jurisprudenceReturnTypes.SPECIES_NAME,
      label: <Translation>{(translate) =>  translate('species_name')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 1,
    },
    {
      name: jurisprudenceReturnTypes.ARTICLE,
      label: <Translation>{(translate) =>  translate('article')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 2,
    },
    {
      name: jurisprudenceReturnTypes.DECISION_DATE,
      label: <Translation>{(translate) =>  translate('decision_date')}</Translation>,
      enabled: true,
      isDefault: true,
      disableSortBy: true,
      defaultOrder: 3
    },
    {
      name: jurisprudenceReturnTypes.OUTCOME,
      label: <Translation>{(translate) =>  translate('outcome')}</Translation>,
      enabled: true,
      isDefault: true,
      disableSortBy: true,
      defaultOrder: 4
    }
  ];

  const [excelModalShow, setExcelModalShow] = useState(false);
  const handleExcelModalClose = () => setExcelModalShow(false);
  const handleExcelModalShow = () => setExcelModalShow(true);
  const [completedPromises, setCompletedPromises] = useState<number>(0);

  const [generateExcel, setGenerateExcel] = useState(false);
  const generateExcelRef = React.useRef(false);
  generateExcelRef.current = generateExcel;

  const limiter = new Bottleneck({
    minTime: 200,
    maxConcurrent: 3,
  });

  const completedPromisesRef = React.useRef(completedPromises);

  const generatePromises = async (values: any, totalItems: number) => {
    completedPromisesRef.current = 0;
    let i = 0;
    let pageIdx = 0;
    const promiseArray: Array<Promise<any>> = [];
    while (i < totalItems) {
      const body = getSearchJurisprudenceQuery(values, 1000, pageIdx);
      const promise = limiter.schedule(() =>
        graphQLClientInstance.post(API_URLS.GRAPHQL, body)
      );
      promiseArray.push(promise);
      i += 1000;
      pageIdx++;
    }

    let dataArr: Array<any> = [];
    await axios.all(promiseArray).then(
      axios.spread((...res) => {
        dataArr = res.map((r) => r.data.data.jurisprudenceSearch.data);
      })
    );
    return dataArr;
  };

  const completedPromisesCounter = (count: number) => {
    completedPromisesRef.current = count;
    setCompletedPromises(count);
  };

  limiter.on("done", function (info) {
    completedPromisesCounter(completedPromisesRef.current + 1);
  });

  const downloadExcelFile = async ({ values }: { values: any }) => {
    setGenerateExcel(false);
    let myData: any[] = [];
    await generatePromises(values, totalCountRef.current)
      .then((res) => {
        if (res) {
          myData = res;
        }
      })
      .catch((err) => {
        console.log(err);
      });

    const flattenData: any[] = myData.flat(2)
    let file = "";
    setGenerateExcel(true);
    console.log(flattenData);
    file = await generatexlx(
      flattenData,
      [
        jurisprudenceReturnTypes.ID,
        jurisprudenceReturnTypes.TITLE,
        jurisprudenceReturnTypes.SPECIES_NAME,
        jurisprudenceReturnTypes.ARTICLE,
        jurisprudenceReturnTypes.DECISION_DATE,
        jurisprudenceReturnTypes.OUTCOME
      ],
      downloadExcelWorksheetTitles.SEARCH_JURISPRUDENCE_WORKSHEET
    ).catch((err) => {
      console.log(err);
    });

    if (file) {
      const blob = new Blob([file], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadBlob(blob, downloadExcelFileNames.SEARCH_JURISPRUDENCE);
    }
    setExcelModalShow(false);
  };

  useEffect(() => {
    if (completedPromises) {
      handleExcelModalShow();
    }
  }, [completedPromises]);

  const downloadExcel = React.useCallback(
    ({ values }) => {
      downloadExcelFile({ values: values });
    }, []
  );

  const downloadExcelModal = (
    <Modal
      centered
      keyboard={false}
      backdrop="static"
      show={excelModalShow}
      onHide={handleExcelModalClose}
    >
      <Modal.Header>
        <Modal.Title>{t("downloading_results")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProgressBar
          className="w-100"
          now={calcPercentage(completedPromises, totalCount / 1000)}
          label={`${calcPercentage(completedPromises, totalCount / 1000)}%`}
        />
        <br></br>
        <div>
          {t("results_downloaded_counter", {
            current: calcRemaining(completedPromises * 1000, totalCount),
            total: totalCount,
          })}
        </div>
        <div>
          {generateExcel?
            <>
              {t("generate_excel")} <Spinner animation="border" size="sm" variant="primary" />
            </>
          :""}
        </div>
      </Modal.Body>
    </Modal>
  );

  const tableColumns = React.useMemo(() => {
    return [
      ...selectedColumns.map((item: any) => { return {...item, Header: item.label, accessor: item.name}})
    ]
  }, [selectedColumns]);

  return (
    <>
    <Loader show={isLoading}></Loader>
      <CustomBackground  image={backgroundImages.MY_TESTS}>
        <SearchFormContainer
          title={t("jurisprudence_list.title")}
          subtitle={t("jurisprudence_list.subtitle")}
          >
          {form}
        </SearchFormContainer>
      </CustomBackground>
      <Container fluid style={{display: !(isLoading && totalCount === 0)?'block':'none'}} className={classes.resultsTable}>
        <ResultsHeader totalCount={totalCount} resultsRef={resultsRef} />
        <Table
          columns={tableColumns}
          origin={tableOrigin.JURISPRUDENCE}
          hideColumnsButton={true}
          isSortable={false}
          data={data}
          fetchData={fetchData}
          totalCount={totalCount}
          pageCount={pageCount}
          refreshData={refreshData}
          downloadExcel={downloadExcel}
          formValues={formValues}
          showPagination={true}
          pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        />
        {downloadExcelModal}
      </Container>
    </>
  )
}

const getJurisprudence = (setJurisprudenceOptions: any) => {
  graphQLClientInstance.post(API_URLS.GRAPHQL, {
    query: `
    {
      jurisprudencepre {
        data {
          key
          subheadings
        } total
      }
    }`}).then((res: any) => {
      const jurisprudenceObj: any = {};
      res.data.data?.jurisprudencepre?.data?.forEach((val: any) => {
        jurisprudenceObj[val.key] = val.subheadings;
      })
    sessionStorage.setItem(JURISPRUDENCE_SESSION_KEY, JSON.stringify(jurisprudenceObj));
    setJurisprudenceOptions(jurisprudenceObj);
  }).catch((err: any) => { console.log(err); } )
}

export default withRouter(Jurisprudence);
